import moment from "moment";
import { buildDemoStudentsForClass } from "./buildDemoStudents";
import { DEMO_TURMA_DISC } from "./buildDemoOccurrences";

/**
 * Mesmo agrupador usado nas colunas (AGRUPADOR no TOTVS / group no grid).
 */
const GROUP_NAME = "Português — demonstração";

const DEMO_COD_COLIGADA = 1;
const DEMO_COD_ETAPA = 1;

/**
 * Formato do id de coluna (WAYBEEDIARYV1.FH / GridFrequency): o renderHeader faz
 * split(";") e usa os três últimos segmentos como hora início, hora fim e data.
 */
function buildColumnId({
  codColigada,
  idTurmaDisc,
  idHorarioTurma,
  codEtapa,
  diaSemana,
  horaIni,
  horaFim,
  dateStr
}) {
  return `${codColigada};${idTurmaDisc};${idHorarioTurma};${codEtapa};${diaSemana};${horaIni};${horaFim};${dateStr}`;
}

/** Duas aulas geminadas por dia (fim de uma = início da próxima, como no LoopBack). */
const SLOT_TIMES = [
  { horaIni: "08:00", horaFim: "09:00" },
  { horaIni: "09:00", horaFim: "10:00" }
];

/**
 * Resposta GET ClassDiaryV1/gridFrequency no formato já transformado pelo waybee-api
 * (arrays em groups, headers e lines).
 *
 * @param {string|number|null|undefined} idTurmaDisc
 * @param {string|null|undefined} dateFrom ex.: 2026-04-12T00:00:00
 * @param {string|null|undefined} dateTo
 * @returns {{ groups: object[], headers: object[], lines: any[][] }}
 */
export function buildDemoGridFrequency(idTurmaDisc, dateFrom, dateTo) {
  if (idTurmaDisc == null || String(idTurmaDisc) !== String(DEMO_TURMA_DISC)) {
    return {
      groups: [],
      headers: [],
      lines: []
    };
  }

  const from = moment(dateFrom);
  const to = moment(dateTo);
  if (!from.isValid() || !to.isValid()) {
    return {
      groups: [],
      headers: [],
      lines: []
    };
  }

  const groups = [
    { header: GROUP_NAME, name: GROUP_NAME, order: 0 }
  ];

  const start = from.clone().startOf("day");
  const last = to.clone().startOf("day");
  const daysCount = last.diff(start, "days") + 1;

  if (daysCount < 1) {
    return {
      groups: [],
      headers: [],
      lines: []
    };
  }

  let horarioSeq = 5001;
  const dayMoments = Array.from({ length: daysCount }, (_, i) =>
    start.clone().add(i, "day")
  );

  const headers = dayMoments.flatMap(dayM => {
    const dayOfWeek = dayM.isoWeekday();
    const dateStr = dayM.format("YYYY-MM-DD");
    const dayLabel = dayM.clone().locale("pt-br").format("dddd");

    return SLOT_TIMES.map(({ horaIni, horaFim }) => {
      const idHorarioTurma = horarioSeq;
      horarioSeq += 1;
      const id = buildColumnId({
        codColigada: DEMO_COD_COLIGADA,
        idTurmaDisc: DEMO_TURMA_DISC,
        idHorarioTurma,
        codEtapa: DEMO_COD_ETAPA,
        diaSemana: dayOfWeek,
        horaIni,
        horaFim,
        dateStr
      });

      return {
        id,
        header: id,
        title: "Português",
        description: dayLabel,
        schedule: `${horaIni} - ${horaFim}`,
        group: GROUP_NAME,
        date: dateStr,
        double: "false",
        horaInicial: horaIni,
        horaFinal: horaFim,
        codColigada: DEMO_COD_COLIGADA,
        idTurmaDisc: DEMO_TURMA_DISC,
        idHorarioTurma,
        codEtapa: DEMO_COD_ETAPA,
        diaDaSemana: dayOfWeek,
        enabled: "true",
        aulasDadas: "18",
        aulasPrevistas: "40"
      };
    });
  });

  for (let i = 0; i < headers.length; i += 2) {
    const header = headers[i];
    const pair = headers[i + 1];
    if (pair && header.date === pair.date) {
      header.double = "true";
      header.doubleRelatedIds = [pair.id];
      pair.double = "true";
      pair.doubleRelatedIds = [header.id];
    }
  }

  const students = buildDemoStudentsForClass("DEMO-T1");
  const morningHeaderIdsByDate = {};
  headers.forEach(header => {
    if (header.schedule === "08:00 - 09:00" && header.date) {
      morningHeaderIdsByDate[header.date] = header.id;
    }
  });

  const lines = students.map((student, rowIndex) => {
    const control = {
      columnId: "studentId",
      studentId: student.ra,
      name: student.name,
      photoUrl: student.photo,
      // Demo: último aluno como desistente para validar o destaque cinza.
      desistente: rowIndex === students.length - 1 ? "S" : "N"
    };

    const cells = headers.map(h => {
      const cell = {
        columnId: h.id,
        studentId: student.ra,
        idTurmaDisc: DEMO_TURMA_DISC,
        idHorarioTurma: h.idHorarioTurma,
        rollCallNumber: rowIndex + 1
      };

      // Demo: primeiro aluno falta na aula da manhã para validar o destaque na tarde.
      if (
        rowIndex === 0 &&
        morningHeaderIdsByDate[h.date] === h.id
      ) {
        cell.absence = "false";
      }

      return cell;
    });

    return [control, ...cells];
  });

  return {
    groups,
    headers,
    lines,
    periodSummaries: [
      {
        codEtapa: String(DEMO_COD_ETAPA),
        name: "1º Período",
        aulasPrevistas: "40",
        aulasDadas: "18"
      }
    ]
  };
}
